import React, { useCallback, useEffect, useState } from "react";
import { MessageSquare, Moon, Search, Sun } from "lucide-react";
import CommandPalette from "./components/CommandPalette";
import ProjectPage from "./components/ProjectPage";
import {
  About,
  Contact,
  Experience,
  Hero,
  Ledger,
  Toolkit,
  Work,
  useActiveSection,
} from "./components/Sections";
import { findProject, profile, t, ui } from "./data/portfolio";
import { startVisit, trackPage } from "./visit";
import "./App.css";

const SECTIONS = ["work", "ledger", "experience", "toolkit", "contact"];
const PROJECT_PREFIX = "#/proje/";

/* Kütüphanesiz hash yönlendirme.
   #/proje/<slug> → proje sayfası. Diğer her şey (#work, #contact…) ana sayfa. */
function useHashRoute() {
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  if (hash.startsWith(PROJECT_PREFIX)) {
    return { view: "project", slug: decodeURIComponent(hash.slice(PROJECT_PREFIX.length)) };
  }
  return { view: "home", anchor: hash.startsWith("#") ? hash.slice(1) : "" };
}

function App() {
  const [theme, setTheme] = useState("dark");
  const [lang, setLang] = useState("tr");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [seedQuestion, setSeedQuestion] = useState(null);
  const [isMac, setIsMac] = useState(false);
  const [heroVisible, setHeroVisible] = useState(true);

  const labels = ui[lang];
  const route = useHashRoute();
  const onHome = route.view === "home";
  const active = useActiveSection(SECTIONS, route.view + (route.slug || ""));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    setIsMac(/Mac|iPhone|iPad/.test(window.navigator.platform || ""));
  }, []);

  /* Ziyaret bildirimi: ilk açılışta "giriş", sonra her proje sayfası */
  const pageName = onHome ? "Ana sayfa" : t(findProject(route.slug)?.name, "tr") || route.slug;
  useEffect(() => {
    startVisit(pageName);
    trackPage(pageName);
  }, [pageName]);

  /* Proje sayfasından ana sayfaya bir bölüm bağlantısıyla dönüldüğünde,
     bölüm ancak render edildikten sonra kaydırılabilir. */
  useEffect(() => {
    if (!onHome || !route.anchor) return;
    const el = document.getElementById(route.anchor);
    if (el) window.requestAnimationFrame(() => el.scrollIntoView({ block: "start" }));
  }, [onHome, route.anchor]);

  /* Hero'da zaten bir "Onur'a sor" var — kürek, hero ekrandan çıkınca gelsin.
     Proje sayfalarında hero yok, o yüzden hep görünür. */
  useEffect(() => {
    if (!onHome) {
      setHeroVisible(false);
      return;
    }
    const hero = document.getElementById("top");
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, [onHome]);

  const openPalette = useCallback((question = null) => {
    setSeedQuestion(typeof question === "string" ? question : null);
    setPaletteOpen(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSeedQuestion(null);
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="page">
      <a className="skip" href="#work">
        {labels.nav.work}
      </a>

      <nav className="topbar">
        <a className="topbar-name" href="#top">
          {profile.name}
        </a>

        <div className="topbar-links">
          {SECTIONS.filter((s) => s !== "ledger").map((id) => (
            <a
              key={id}
              href={`#${id}`}
              className={onHome && active === id ? "is-active" : ""}
            >
              {labels.nav[id]}
            </a>
          ))}
        </div>

        <div className="topbar-tools">
          <button
            type="button"
            className="searchbar"
            onClick={() => openPalette()}
            aria-label={labels.searchHint}
          >
            <Search size={15} strokeWidth={1.8} />
            <span className="searchbar-text">{labels.searchHint}</span>
            <kbd>{isMac ? "⌘" : "Ctrl"} K</kbd>
          </button>

          <button
            type="button"
            className="icon-btn"
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            aria-label={lang === "tr" ? "Switch to English" : "Türkçe'ye geç"}
          >
            {lang === "tr" ? "EN" : "TR"}
          </button>

          <button
            type="button"
            className="icon-btn"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={theme === "dark" ? labels.theme.toLight : labels.theme.toDark}
          >
            {theme === "dark" ? <Sun size={16} strokeWidth={1.8} /> : <Moon size={16} strokeWidth={1.8} />}
          </button>
        </div>
      </nav>

      {onHome ? (
        <main className="wrap">
          <Hero lang={lang} onAsk={openPalette} />
          <Work lang={lang} />
          <Ledger lang={lang} />
          <Experience lang={lang} />
          <Toolkit lang={lang} />
          <About lang={lang} />
          <Contact lang={lang} onAsk={openPalette} />
        </main>
      ) : (
        <ProjectPage slug={route.slug} lang={lang} onAsk={openPalette} />
      )}

      <button
        type="button"
        className={`ask-fab ${heroVisible ? "is-hidden" : ""}`}
        onClick={() => openPalette()}
        aria-label={labels.palette.ask}
      >
        <MessageSquare size={17} strokeWidth={1.9} />
        <span>{labels.palette.ask}</span>
      </button>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        lang={lang}
        initialQuestion={seedQuestion}
      />
    </div>
  );
}

export default App;
